import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getQuizzes, deleteQuiz, createQuiz, updateQuiz } from "../../../utils/api";
import "./Quizzes.css";

const columns = [
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
  { 
    key: "questions", 
    label: "Questions",
    render: (value) => value ? value.length : 0
  },
  { key: 'status', label:'Status'},
];

const filters = [
  {
    key: 'status',
    label: 'Filter by Status',
    type: 'select',
    options: [
      { value: 'active', label: 'Active' },
      { value:'inactive', label:"Inactive"}
   ]
 }
];

// Convert frontend question format to backend schema format
function convertQuestionsToBackend(questions) {
 return questions.map(q => ({
   questionText : q.question,
   options : q.options.map((optText,i)=>({
     text : optText,
     isCorrect:i===parseInt(String(q.correctAnswer))
   })),
   explanation:""
 }));
}

// Convert backend question format to frontend display format
function convertQuestionFromBackend(q) {
  const options = Array.isArray(q.options)
    ? q.options.map((opt) => opt?.text || "")
    : [];

  const correctAnswer = Array.isArray(q.options)
    ? q.options.findIndex((opt) => opt?.isCorrect === true)
    : -1;

  return {
    question: q.questionText || "",
    options: [...options, "", "", ""].slice(0, 4),
    correctAnswer: correctAnswer >= 0 ? correctAnswer : -1,
    explanation: q.explanation || "",
  };
}

const initialFormData={
 title:"",
 description:"",
 status :"active",
 questions:[]
};

const initialQuestion={
 question:"",
 options:["","","",""],
 correctAnswer:-1
};

export default function Quizzes() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  const [formData, setFormData] = useState(initialFormData);
  const [currentQuestion, setCurrentQuestion] = useState(initialQuestion);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setFormData(initialFormData);
    setCurrentQuestion(initialQuestion);
    setEditingQuestionIndex(null);
    setSelectedQuiz(null);
  };

  const openAddModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const openEditModal = (quiz) => {
    setSelectedQuiz(quiz);
    setIsEditMode(true);
    setFormData({
      title: quiz.title || "",
      description: quiz.description || "",
      status: quiz.status || "active",
      questions: (quiz.questions || []).map(convertQuestionFromBackend),
    });
    setCurrentQuestion(initialQuestion);
    setEditingQuestionIndex(null);
    setIsModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuestionChange = (e) => {
    const { name, value } = e.target;

    if (name === "question") {
      setCurrentQuestion((prev) => ({ ...prev, question: value }));
      return;
    }

    if (name === "correctAnswer") {
      setCurrentQuestion((prev) => ({ ...prev, correctAnswer: parseInt(value, 10) }));
      return;
    }

    const optionIndex = parseInt(name.replace("option", ""), 10);
    if (!isNaN(optionIndex)) {
      setCurrentQuestion((prev) => {
        const updatedOptions = [...prev.options];
        updatedOptions[optionIndex] = value;
        return { ...prev, options: updatedOptions };
      });
    }
  };

  const addOrUpdateQuestion = () => {
    if (!currentQuestion.question.trim()) {
      alert("Please enter a question.");
      return;
    }

    const hasAtLeastTwoOptions = currentQuestion.options.filter((opt) => opt.trim()).length >= 2;
    if (!hasAtLeastTwoOptions) {
      alert("Please provide at least two options.");
      return;
    }

    const updatedQuestions = [...formData.questions];
    const questionPayload = {
      ...currentQuestion,
      options: currentQuestion.options.slice(0, 4),
    };

    if (editingQuestionIndex !== null && editingQuestionIndex >= 0) {
      updatedQuestions[editingQuestionIndex] = questionPayload;
    } else {
      updatedQuestions.push(questionPayload);
    }

    setFormData((prev) => ({ ...prev, questions: updatedQuestions }));
    setCurrentQuestion(initialQuestion);
    setEditingQuestionIndex(null);
  };

  const handleEditQuestion = (index) => {
    setEditingQuestionIndex(index);
    setCurrentQuestion(formData.questions[index] || initialQuestion);
  };

  const handleDeleteQuestion = (index) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
    if (editingQuestionIndex === index) {
      setEditingQuestionIndex(null);
      setCurrentQuestion(initialQuestion);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      title: formData.title,
      description: formData.description,
      status: formData.status,
      questions: convertQuestionsToBackend(formData.questions),
    };

    try {
      if (isEditMode && selectedQuiz) {
        await updateQuiz(selectedQuiz._id, payload);
        alert("Quiz updated successfully!");
      } else {
        await createQuiz(payload);
        alert("Quiz created successfully!");
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save quiz");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content quiz-page">
        <AdminList
          title="Quizzes"
          fetchData={getQuizzes}
          deleteData={deleteQuiz}
          columns={columns}
          filters={filters}
          onAdd={openAddModal}
          onEdit={openEditModal}
          onView={openEditModal}
          searchPlaceholder="Search quizzes..."
        />

        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={isEditMode ? "Edit Quiz" : "Add New Quiz"}
            size="large"
          >
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>Quiz Details</h3>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    required
                    placeholder="Enter quiz title"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows="2"
                    placeholder="Enter description"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleFormChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h3>Questions</h3>
                <div className="question-builder">
                  <div className="form-group">
                    <label>Question *</label>
                    <input
                      type="text"
                      name="question"
                      value={currentQuestion.question}
                      onChange={handleQuestionChange}
                      placeholder="Enter question text"
                    />
                  </div>
                  <div className="form-group">
                    <label>Options</label>
                    {currentQuestion.options.map((opt, idx) => (
                      <div key={idx} className="option-row">
                        <input
                          type="text"
                          name={`option${idx}`}
                          value={opt}
                          onChange={handleQuestionChange}
                          placeholder={`Option ${idx + 1}`}
                        />
                        <label>
                          <input
                            type="radio"
                            name="correctAnswer"
                            value={idx}
                            checked={currentQuestion.correctAnswer === idx}
                            onChange={handleQuestionChange}
                          />
                          Correct
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setCurrentQuestion(initialQuestion);
                        setEditingQuestionIndex(null);
                      }}
                    >
                      Clear Question
                    </button>
                    <button type="button" className="btn btn-primary" onClick={addOrUpdateQuestion}>
                      {editingQuestionIndex !== null ? "Update Question" : "Add Question"}
                    </button>
                  </div>
                </div>

                {formData.questions.length > 0 && (
                  <div className="question-list">
                    <h4>Added Questions</h4>
                    {formData.questions.map((q, idx) => (
                      <div key={idx} className="question-item">
                        <div className="question-text">
                          {idx + 1}. {q.question}
                        </div>
                        <div className="question-actions">
                          <button type="button" onClick={() => handleEditQuestion(idx)} className="edit-btn">
                            Edit
                          </button>
                          <button type="button" onClick={() => handleDeleteQuestion(idx)} className="delete-btn">
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving..." : isEditMode ? "Update Quiz" : "Create Quiz"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </main>
    </div>
  );
}
